// Educational Facility Master Plan Map
// web services
// 11/2023
// 02/2025 - v2
// 06/2025 - global library updates
//  04/2026 - global ui updates

import {setOptions, importLibrary} from "@googlemaps/js-api-loader";
import $ from "jquery";
import {Tooltip} from "bootstrap";
import "jquery-ui/dist/jquery-ui.min";
import DataTable from "datatables.net-dt";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
    animateMarker,
    getAPIValues,
    googleAPIKey,
    initModal,
    inputSubstringMatch,
    menuHighlight,
    percentDisplay,
    positionMarkersOnMap,
    scrollToRow,
    setMapBounds,
    showPolygon,
    stopAnimateMarker,
    toSnakeCase,
    updateElement,
    POLYGON_STYLE,
    $loader,
    $resultTable,
    $strong,
    chicago,
    color,
    $targets,
    $addressDirectionLink
} from "../../../global/map-library";
import {
    USDollar,
    USMillionDollar,
    datasetTemplate,
    TABLE_COLUMNS,
    donutChartOptionsTemplate,
    schoolTypes,
} from "./constants";
import {
    $checkMark,
    populateFilterMenu,
    schoolStyle,
    schoolClass,
    convertToInternationalCurrencySystem,
    $chartElements,
} from "./helper";
import "../../../global/map-library.scss"
import "../scss/styles.scss";
import markerSvg from "../../../global/images/marker_pin.svg"

// extend the $targets object with elements unique to the EFMP map
$targets.regionSelect = $("#regionMenuSelect");
$targets.showAllRegions = $("#showAllRegions");
$targets.wardSelect = $("#wardMenuSelect");
$targets.showAllWards = $("#showAllWards");
$targets.schoolTypeSelect = $("#schoolTypeSelect");
$targets.utilizationSelect = $("#utilizationSelect");
$targets.facilitySelect = $("#facilitySelect");
$targets.resultTabs = $("#nav-tab");
$targets.chartArea = $("#chart-area");
$targets.chartEfmp = $("#chart-efmp");
$targets.chartUtilization = $("#chart-utilization");
$targets.chartCapital = $("#chart-capital");
$targets.schoolSearch = $("#school-search");
$targets.searchForm = $("#search-form");

Chart.register(ChartDataLabels);
// DataTable.type("num", "className", "dt-head-left");
// DataTable.type("num-fmt", "className", "dt-head-left");
const urlParams = new URLSearchParams(window.location.search);
const fullscreen = urlParams.get("fullscreen");

// add selection highlighting to jquery ui autocomplete menu
$.ui.autocomplete.prototype._renderItem = menuHighlight;

setOptions({
    key: googleAPIKey("AIzaSyDVOJtjwHsXGlwkyR9kKhmC9B2cLbAd0e4"), // EFMP Map key - this should be unique for every project
    v: "weekly",
});

try {
    ///////////////////////////////////// GOOGLE MAP LIBRARIES /////////////////////////////////////

    const {Map, InfoWindow} = await importLibrary("maps");
    const {AdvancedMarkerElement, PinElement} = await importLibrary("marker");

    ///////////////////////////////////// GLOBALS /////////////////////////////////////

    const EFMP_SCHOOLS_API = "/api/efmpschools/lists";
    const CAPITAL_PERCENTAGE_API = "/api/efmpschools/capitalpercentage";
    const EFMP_PERCENTAGE_API = "/api/efmpschools/efmppercentage";
    const UTILIZATION_PERCENTAGE_API = "/api/efmpschools/utilizationpercentage";
    const MAP_API = "/api/maps/GeoJSON";
    const REGION_API_PARAMETERS = {"mapName": "ARA", "year": 2021};
    const WARD_API_PARAMETERS = {"mapName": "WARD", "year": 2024};

    const markers = []; // hold the data for pins currently displayed on the map as well as the pins themselves under the "advancedMarker" keys
    const labelMarkers = [];
    const charts = {};

    let data;  // global array to hold the data to be displayed. it will be filled with api data, reduced by filtering, reset to the original dataset,
    // or be refilled with radius reduced api data throughout the running of this script

    const infoWindow = new InfoWindow();

    let loaded = false;
    let dataTable; // used for datatables.js initialization

    /*************************************************************************************************************/

    // get efmp data for all schools and location data for all districts and wards
    const [efmpDataAll, regionData, wardData] = await Promise.all([getAPIValues(EFMP_SCHOOLS_API), getAPIValues(MAP_API, REGION_API_PARAMETERS), getAPIValues(MAP_API, WARD_API_PARAMETERS)]);

    // create the map
    const map = new Map($targets.map[0], {
        mapId: "f8a91fc0ae7b0f05",
        center: chicago,
        streetViewControl: false,
        fullscreenControl: false,
        zoom: 11
    });

    ///////////////////////////////////// CREATE REMAINING UI ELEMENTS /////////////////////////////////////

    // create region, ward, and utilization menus from the data
    // school type and facility status menus need custom mapping, so they are hard-coded in index.html
    const regionList = [{label: "District", value: "0"}];
    regionData.features.forEach(region => {
        regionList.push({label: region.properties.Name, value: region.properties.ES_ZONENUM});
    });
    regionList.sort((a, b) => {
        if (Number(a.value) > Number(b.value)) {
            return 1;
        } else if (Number(a.value) < Number(b.value)) {
            return -1;
        } else {
            return 0;
        }
    });

    populateFilterMenu($targets.regionSelect, regionList, "0");

    //create ward menu
    const wardList = [{label: "All", value: "0"}];
    wardData.features.forEach(wardFeature => {
        wardList.push({label: wardFeature.properties.WARD, value: wardFeature.properties.WARD});
    });
    populateFilterMenu($targets.wardSelect, wardList, "0");

    const utilizationList = [{label: "All", value: "all"}];
    // add the unique utilizationStatus values from efmpDataAll to the list
    [...new Set(efmpDataAll.map(s => s.utilizationStatus))].forEach(f => utilizationList.push({
        label: f,
        value: f
    }));
    populateFilterMenu($targets.utilizationSelect, utilizationList, "all");

    // initialize the school autocomplete menu
    const schoolNames = efmpDataAll.map(school => school.schoolName);
    initializeAutocomplete(schoolNames);

    // Update the page header with cms data
    updateElement($("h1", $targets.overlay), window.appConfig.pageHeading);

    // update the tab text with cms data. In this case, wrap each tab's text in the cms with a div with an id matching the tab to create three separate text fields
    $(window.appConfig.pageContent).each(function () {
        const $elem = $(this);
        $(`#${$elem.attr("id")} .tab-text`).html($elem.html());
    });

    ///////////////////////////////////// SHOW UI /////////////////////////////////////
    if (fullscreen) {
        $targets.mainWrapper.toggle(false);
        $targets.displayToggle.attr("aria-expanded", (index, attr) => (attr === "true" ? "false" : "true"));
    }
    $targets.dataLoader.remove();
    $targets.overlay.removeAttr("hidden");

    ///////////////////////////////////// EVENTS /////////////////////////////////////
    // pan the map into the proper position after everything is drawn
    map.addListener("tilesloaded", () => {
        if (!loaded) {
            setTimeout(() => {
                // fires more reliably when wrapped in an asynchronous call. Using a delay 0 in this case
                positionMarkersOnMap(map, $targets);
                loaded = true;
            }, 0);
        }
    });

    // resize the labels after the map is done with any updates
    google.maps.event.addListener(map, "idle", function () {
        $(".map-label").css({"font-size": map.getZoom() >= 12 ? "24px" : "14px"});
    });

    // remove the table row highlight and clear the input field when the infowindow is closed
    infoWindow.addListener("close", () => {
        $targets.schoolInput.val("");
        $("tr.selected-row").removeClass("selected-row");
    });

    // school search button clicked
    $targets.schoolSearch.on("click", () => {
        schoolSearch($targets.schoolInput.val());
    });

    // school input field enter key
    $targets.schoolInput.on("keydown", async (e) => {
        if (e.key === "Enter") { // Check if the Enter key is pressed
            if ($targets.schoolInput.autocomplete) {
                $targets.schoolInput.autocomplete("close");
            }
            schoolSearch($targets.schoolInput.val());
        }
    });

    // update the results and map pins when filter menus are changed
    $(".filter-menu").on("input", async (e) => await updateFilters(e));

    // update the results and map pins when tabs are changed
    $(".nav-link", $targets.resultTabs).on("click", async (e) => await updateFilters(e));

    // deal with the show regions/show wards buttons
    $(".filter-button").on("click", async function (e) {
        $(this).data("show-areas", !$(this).data("show-areas")); // toggle the data attribute on the clicked button
        await updateFilters(e);
    });

    // reset button
    $targets.reset.on("click", resetMap);

    // collapse main window
    $targets.displayToggle.on("click", async (e) => {
        $targets.mainWrapper.toggle({
            duration: 0,
            complete: async () => {
                $targets.displayToggle.attr("aria-expanded", (index, attr) => (attr === "true" ? "false" : "true"));
                await updateFilters();
                // scrollToRow($("tr.selected-row"), $(".tab-pane.active .results"));
            }
        });
    });

    // resize the results list and move the markers if the window size changes
    $(window).on("resize", async () => {
        await updateFilters();
    });

    ///////////////////////////////////// TRIGGER INITIAL DRAW /////////////////////////////////////
    await updateFilters();

    ///////////////////////////////////// FUNCTIONS /////////////////////////////////////

    // updateFilters() is the main ui update routine. It is called every time there is a state change
    // It goes through the following steps:
    // read the current menu selections from the ui as a set of filters; filters = ...
    // adjust the UI based on user input. In this case, set the district selection to "all" if a ward is selected and vice versa and set the show all district/ward buttons; adjustFilterUI()
    // reduce the main data set based on the filters from step 1; applyFilters()
    // set and show the markers based on the reduced set; setMarkers()
    // show any selected polygons
    // adjust the map zoom based on the contents
    // adjust the map position based on the window size
    // update the charts

    async function updateFilters(e) {
        // get the filters from the current state of the UI
        const filters = {
            region: Number($targets.regionSelect.val()),
            ward: Number($targets.wardSelect.val()),
            schoolType: $targets.schoolTypeSelect.val(),
            facility: $targets.facilitySelect.val(),
            utilization: $targets.utilizationSelect.val(),
            showAllRegions: $targets.showAllRegions.data("show-areas"),
            showAllWards: $targets.showAllWards.data("show-areas"),
        }

        adjustFilterUI(e);

        data = applyFilters();

        infoWindow.close();

        $targets.schoolInput.val("");

        setMarkers(data);

        updateResults(markers);

        showSelectedPolygons();

        autoSetMapBounds(filters);

        positionMarkersOnMap(map, $targets);

        await updateCharts();

        /////////////////////////////////////////////////  UPDATE FILTER FUNCTIONS   ///////////////////////////////////////////////////////////////

        // set filters based on each other's states
        function adjustFilterUI(e) {
            // find out which filter was changed based on the triggering event and refer to it by its id
            const changedFilter = e ? $(e.target).attr("id") : undefined;

            // select all wards if a region is selected
            if (filters.region !== 0 && changedFilter === "regionMenuSelect") {
                filters.ward = 0;
            }

            // select all districts if a ward is selected
            if (filters.ward !== 0 && changedFilter === "wardMenuSelect") {
                filters.region = 0;
            }

            // reset any displays of all polygons if a ward or region menu is selected
            if (changedFilter === "regionMenuSelect" || changedFilter === "wardMenuSelect") {
                filters.showAllRegions = false;
                filters.showAllWards = false;
            }

            // if a show all regions button is clicked, reset the show all wards button and vice versa
            if (filters.showAllRegions && changedFilter === "showAllRegions") {
                filters.showAllWards = false;
            }
            if (filters.showAllWards && changedFilter === "showAllWards") {
                filters.showAllRegions = false;
            }

            // reset any individually selected wards or regions if a show all button is selected
            if (filters.showAllRegions || filters.showAllWards) {
                filters.ward = 0;
                filters.region = 0;
            }

            // update the UI based on the filter state
            $targets.regionSelect.val(filters.region);
            $targets.wardSelect.val(filters.ward);
            $targets.showAllRegions.data("show-areas", filters.showAllRegions);
            $targets.showAllWards.data("show-areas", filters.showAllWards);
            $targets.showAllRegions.text(filters.showAllRegions ? "Hide Regions" : "Show Regions");
            $targets.showAllWards.text(filters.showAllWards ? "Hide Wards" : "Show Wards");
        }

        // handles logic for what type of polygon(s) to draw
        function showSelectedPolygons() {
            // remove any existing polygons
            map.data.forEach(function (feature) {
                map.data.remove(feature);
            });

            // clear any existing label markers
            labelMarkers.forEach(l => l.setMap(null));
            labelMarkers.length = 0;

            if (filters.showAllRegions || filters.showAllWards) {
                showAllPolygons();
            }

            let feature;
            let label;

            if (filters.region !== 0) {
                feature = regionData.features.find(f => f.properties.ES_ZONENUM === filters.region.toString());
                label = feature.properties.Name;
            } else if (filters.ward !== 0) {
                feature = wardData.features.find(f => f.properties.WARD === filters.ward.toString());
                label = feature.properties.WARD;
            }
            if (feature) {
                const polygon = showPolygon(map, feature, POLYGON_STYLE(), label);
                labelMarkers.push(polygon.labelMarker);
            }
        }

        // add all region or ward polygons to the map with rollover and click events and remove markers
        function showAllPolygons() {
            // remove any existing markers from the map
            markers.forEach(m => m.map = null);
            markers.length = 0;

            map.data.addListener("click", async function (event) {
                if (filters.showAllRegions) {
                    $targets.regionSelect.val(event.feature.getProperty("ES_ZONENUM"));
                    $targets.wardSelect.val("0");
                    $targets.showAllRegions.data("show-areas", false);
                } else if (filters.showAllWards) {
                    $targets.wardSelect.val(event.feature.getProperty("WARD"));
                    $targets.regionSelect.val("0");
                    $targets.showAllWards.data("show-areas", false);
                }
                await updateFilters();
            });

            map.data.addListener("mouseover", (event) => {
                map.data.overrideStyle(event.feature, POLYGON_STYLE(true, true));
            });

            map.data.addListener("mouseout", (event) => {
                map.data.overrideStyle(event.feature, POLYGON_STYLE(true, false));
            });

            if (filters.showAllRegions) {
                regionData.features.forEach(f => {
                    const polygon = showPolygon(map, f, POLYGON_STYLE(true, false), f.properties.Name);
                    labelMarkers.push(polygon.labelMarker);
                });

            } else if (filters.showAllWards) {
                wardData.features.forEach(f => {
                    const polygon = showPolygon(map, f, POLYGON_STYLE(true, false), f.properties.WARD);
                    labelMarkers.push(polygon.labelMarker);
                });
            }
        }

        // filter the complete dataset from efmpDataAll based on the filters array
        function applyFilters() {
            // we will start with the full dataset from efmpDataAll and reduce it sequentially for each filter
            let filteredData = [...efmpDataAll];
            if (filters.region !== 0) {
                filteredData = filteredData.filter(item => item.regionId === filters.region);
            } else if (filters.ward !== 0) {
                filteredData = filteredData.filter(item => item.wardId === filters.ward);
            }

            if (filters.schoolType !== "all") {
                filteredData = filteredData.filter(item => item.type === filters.schoolType);
            }

            const activeTab = $(".nav-link.active", $targets.resultTabs).attr("id");

            if (filters.facility !== "all" && activeTab === "nav-facility-tab") {
                filteredData = filteredData.filter(item => item[filters.facility] === true);
            }

            if (filters.utilization !== "all" && activeTab === "nav-utilization-tab") {
                filteredData = filteredData.filter(item => item.utilizationStatus === filters.utilization);
            }
            return filteredData;
        }

        async function updateCharts() {
            // make sure the chart area is visible before drawing
            $targets.bodyArea.addClass("show");

            // the font size relies on the current width of the chart panel header, it must be defined here because it is based on the current state of the dom
            donutChartOptionsTemplate.options.plugins.datalabels.font.size = $(".chart-panel p").width() > 220 ? 18 : 12;

            await Promise.all([updateEfmp(), updateUtilization(), updateCapital()]);

            async function updateEfmp() {
                if (charts.efmp) {
                    charts.efmp.destroy();
                }
                $(".chart", $targets.chartEfmp).html($loader());
                const parameters = filters.ward !== 0 ? {wardId: filters.ward} : {regionId: filters.region};
                const data = await getAPIValues(EFMP_PERCENTAGE_API, parameters);

                if (data) {
                    $(".chart", $targets.chartEfmp).html($chartElements());
                    charts.efmp = new Chart($("canvas", $targets.chartEfmp)[0], Object.assign({}, donutChartOptionsTemplate));
                    const record = data.find(r => r.type === schoolTypes[filters.schoolType]);
                    if (record) {
                        const dataObj = {
                            labels: ["Immediate", "Long Term", "Facility Upgrades", "ADA"],
                            datasets: [Object.assign({
                                label: "EFMP 2023-33",
                                data: [record.immediateCriticalNeeds05Years, record.longTermCriticalNeeds610Years, record.facilityUpgrades, record.ada],
                                backgroundColor: [color.orange, color.blue, color.yellow, color.purple],
                            }, datasetTemplate)]
                        };
                        updateChart({
                            dataObj: dataObj,
                            chart: charts.efmp,
                            total: `$${convertToInternationalCurrencySystem(record.total)}`
                        });
                    } else {
                        updateChart({
                            dataObj: {},
                            chart: charts.efmp,
                            total: ""
                        });
                    }
                }
            }

            async function updateUtilization() {
                if (charts.utilization) {
                    charts.utilization.destroy();
                }
                $(".chart", $targets.chartUtilization).html($loader());
                const parameters = filters.ward !== 0 ? {wardId: filters.ward} : {regionId: filters.region};
                const data = await getAPIValues(UTILIZATION_PERCENTAGE_API, parameters);

                if (data && data.length > 0) {
                    $(".chart", $targets.chartUtilization).html($chartElements());
                    charts.utilization = new Chart($("canvas", $targets.chartUtilization)[0], Object.assign({}, donutChartOptionsTemplate));
                    const record = data.find(r => r.type === schoolTypes[filters.schoolType]);
                    const utilizationData = [record.efficient, record.underutilized, record.overcrowded, record.na];
                    if (record && utilizationData.some(e => e > 0)) {
                        const dataObj = {
                            labels: ["Efficient", "Underutilized", "Overcrowded", "N/A"], datasets: [Object.assign({
                                label: "Utilization",
                                data: utilizationData,
                                backgroundColor: [color.green, color.orange, color.blue, color.yellow],
                            }, datasetTemplate)]
                        };
                        updateChart({
                            dataObj: dataObj,
                            chart: charts.utilization,
                            total: ""
                        });
                    } else {
                        updateChart({
                            dataObj: {},
                            chart: charts.utilization,
                            total: ""
                        });
                    }
                }
            }

            async function updateCapital() {
                if (charts.capital) {
                    charts.capital.destroy();
                }
                $(".chart", $targets.chartCapital).html($loader());
                const parameters = filters.ward !== 0 ? {wardId: filters.ward} : {regionId: filters.region};
                const data = await getAPIValues(CAPITAL_PERCENTAGE_API, parameters);
                if (data) {
                    $(".chart", $targets.chartCapital).html($chartElements());
                    charts.capital = new Chart($("canvas", $targets.chartCapital)[0], Object.assign({}, donutChartOptionsTemplate));
                    const record = data.find(r => r.type === schoolTypes[filters.schoolType]);
                    if (record) {
                        const dataObj = {
                            labels: ["New Construction", "Exterior", "Site", "MEP", "Interior"],
                            datasets: [Object.assign({
                                label: "Capital Projects 2013-23",
                                data: [record.newConstruction, record.exteriorEnvelope, record.site, record.mepFp, record.interior],
                                backgroundColor: [color.orange, color.blue, color.yellow, color.purple, color.green],
                            }, datasetTemplate)],
                        };
                        updateChart({
                            dataObj: dataObj,
                            chart: charts.capital,
                            total: `$${convertToInternationalCurrencySystem(record.grandTotal)}`
                        });
                    } else {
                        updateChart({
                            dataObj: {},
                            chart: charts.capital,
                            total: ""
                        });
                    }
                }
            }

            // take the incoming data, chart, and colors and trigger a chart redraw
            function updateChart(data) {
                const chart = data.chart;
                chart.data = data.dataObj;
                chart.options.plugins.center_canvas_total = {
                    total: data.total
                }
                chart.update();
            }
        }

        // fill in the results table and perform UI adjustments
        function updateResults(markers) {
            // put the results table into the active tab
            $(".tab-pane.active .results").html($resultTable($tableHead(), $tableBody(markers)));

            // apply the datatables.js library to the results table
            if (dataTable) {
                dataTable.destroy();
            }

            // left-align the table headers by making an array of their column numbers and passing it to the datatables initialization
            const leftAlignTargets = [];
            $(".tab-pane.active .resultList th").each(function (index) {
                leftAlignTargets.push(index);
            });
            dataTable = new DataTable(".tab-pane.active .resultList", {
                paging: false,
                searching: false,
                info: false,
                order: [[0, "asc"]],
                columnDefs: [
                    {
                        targets: leftAlignTargets,
                        className: "dt-head-left"
                    }
                ]
            });

            // set the tooltips for the active results table
            $("[data-bs-toggle=\"tooltip\"]").each(function () {
                new Tooltip(this);
            });

            function $tableHead() {
                const activeTab = $(".nav-link.active", $targets.resultTabs).attr("id");
                const $thead = $("<thead>");

                // create the table header row
                const headerCols = tableColumns(activeTab, TABLE_COLUMNS); // find the columns to display
                const $headerRow = $("<tr>").addClass("results-header");
                headerCols.forEach((col) => {
                    const $cell = $("<th>").attr("data-field-title", col.title);
                    $cell.html(col.title);
                    if (col.title === "School") {
                        $cell.append(` (${markers.length})`); //add (count) to the School header
                    }
                    if (col.tooltipData) {
                        const $infoLink = $("<button>").addClass("fa fa-info-circle").attr({
                            "data-bs-toggle": "tooltip",
                            // "data-bs-html": "true",
                            "data-bs-title": col.tooltipData,
                            // "data-bs-placement": "top",
                            // "data-bs-custom-class": "custom-tooltip"
                        });
                        $cell.append("&nbsp;", $infoLink);
                    }
                    $headerRow.append($cell);
                });

                return $thead.html($headerRow);
            }

            function $tableBody(markers) {
                const activeTab = $(".nav-link.active", $targets.resultTabs).attr("id");
                const headerCols = tableColumns(activeTab, TABLE_COLUMNS); // find the columns to display

                const $tbody = $("<tbody>");

                // make a row for each marker
                markers.forEach(marker => {
                    $tbody.append($resultRow(headerCols, marker));
                });

                return $tbody;

                function $resultRow(headerCols, marker) {
                    const school = marker.school;
                    const $row = $("<tr>").addClass(`resultRow_${toSnakeCase(school.schoolName)} type_${schoolClass(school)}`); // class for filtering based on type
                    headerCols.forEach(col => {
                        const $cell = $("<td>");
                        if (col.title === "School") {
                            const $schoolLink = $("<button>").addClass("d-flex btn btn-link w-100 text-start");
                            $schoolLink.html($("<div>").addClass("marker-icon").html(markerSvg));
                            $schoolLink.append($("<div>").html(school.schoolName));
                            $cell.html($schoolLink);

                            $schoolLink.on("click", () => {
                                allPolygonAdjust(school);
                                openInfoWindow(marker);
                                $row.addClass("selected-row");
                                $targets.schoolInput.val(school.schoolName);
                            });

                        } else {
                            let rowText = "";
                            switch (col.fieldType) {
                                case "bool":
                                    rowText = $checkMark(school[col.fieldName]);
                                    break;
                                case "percent":
                                    rowText = percentDisplay(school[col.fieldName]);
                                    break;
                                case "number": {
                                    rowText = Number(school[col.fieldName]).toLocaleString();
                                    break;
                                }
                                case "currency": {
                                    rowText = USDollar.format(Number(school[col.fieldName]));
                                    break;
                                }
                                default:
                                    rowText = school[col.fieldName];
                                    break;
                            }
                            $cell.html(rowText);
                        }
                        $row.append($cell);
                        $row.on("mouseenter", () => animateMarker(marker));
                        $row.on("mouseleave", () => stopAnimateMarker(marker));
                    });
                    return $row;
                }
            }

            function tableColumns(tabId, columns) {
                return columns.filter(col => col.tab.some(tab => tab === tabId));
            }
        }
    }

    // set the array of map markers based on an incoming set of features
    // this global array (markers) becomes the master source of data for the script
    function setMarkers(schoolData) {
        markers.forEach(m => m.map = null);
        markers.length = 0;
        // create a google maps advancedMarker for each school
        schoolData.forEach(school => {
            const pinStyle = schoolStyle(school);
            pinStyle.scale = .7;
            pinStyle.glyph = "";
            const advancedMarker = new AdvancedMarkerElement({
                map: map,
                position: {lat: Number(school.latitude), lng: Number(school.longitude)},
                title: school.name
            });
            advancedMarker.append(new PinElement(pinStyle));
            advancedMarker.zIndex = 2;
            advancedMarker.school = school;
            advancedMarker.type = school.type;

            // add event listener for the marker information popup
            advancedMarker.addListener("gmp-click", () => {
                openInfoWindow(advancedMarker);
                // set the highlight
                const $selectedRow = $(`.tab-pane.active .resultRow_${toSnakeCase(school.schoolName)}`);
                $selectedRow.addClass("selected-row");

                $("#school-input").val(school.schoolName);
                scrollToRow($selectedRow, $(".tab-pane.active .results"));
            });

            // add this marker to the array of all markers
            markers.push(advancedMarker);
        });
    }

    // reset btn
    function resetMap() {
        $targets.regionSelect.val("0");
        $targets.wardSelect.val("0");
        $targets.schoolTypeSelect.val("all");
        $targets.schoolInput.val("");
        $targets.showAllRegions.attr("data-show-areas", "false");
        $targets.showAllWards.attr("data-show-areas", "false");
        $("#nav-facility-tab").trigger("click");
        updateFilters();
    }

    // control logic for setting map zoom and position based on selected options
    function autoSetMapBounds(filters) {
        if (filters.showAllRegions || filters.showAllWards) {
            setMapBounds(map, regionData); // all regions and all wards should have the same bounds. Using regions
        } else if (filters.region !== 0) {
            setMapBounds(map, regionData.features.find(feature => feature.properties.ES_ZONENUM === filters.region.toString()));
        } else if (filters.ward !== 0) {
            setMapBounds(map, wardData.features.find(feature => feature.properties.WARD === filters.ward.toString()));
        } else {
            setMapBounds(map, markers);
        }
    }

    // perform a search on the markers from the school input field
    function schoolSearch(input) {
        $targets.schoolInput.trigger("blur");

        if (input) {
            const match = inputSubstringMatch(input, data, "schoolName");
            if (match) {
                selectSchool(match.schoolName);
            } else {
                const myModal = new Modal(initModal("No Found School", $("<p>").html(`No schools match "${input}"`)));
                myModal.show();
            }
        } else {
            const myModal = new Modal(initModal("Enter School", $("<p>").html(`Please enter a school`)));
            myModal.show();
        }
    }

    function initializeAutocomplete(schoolData) {
        // reset any existing menu
        $targets.schoolInput.autocomplete({source: []});

        // create an autocomplete menu based on the incoming data
        $targets.schoolInput.autocomplete({
            source: schoolData,
            focus: function (event, ui) { // autocomplete result is focused on
                $targets.schoolInput.val(ui.item.value);
                return false;
            },
            select: (e, ui) => {
                e.preventDefault();
                selectSchool(ui.item.value);
            }
        });
    }

    function selectSchool(val) {
        if ($targets.regionSelect.val() !== "0" || $targets.wardSelect.val() !== "0" || $targets.schoolTypeSelect.val() !== "all") {
            resetMap();
        }
        const foundSchool = data.find(s => s.schoolName === val);
        allPolygonAdjust(foundSchool);
        const foundMarker = markers.find(m => m.school.schoolName === val);
        openInfoWindow(foundMarker);
        $(`.tab-pane.active .resultRow_${toSnakeCase(foundMarker.school.schoolName)}`).addClass("selected-row");
        scrollToRow($("tr.selected-row"), $(".tab-pane.active .results"));
        $targets.schoolInput.val(foundMarker.school.schoolName);
        $targets.schoolInput.trigger("blur");
    }

    // take into account the state where "all regions" or "all wards" are selected.
    // markers don't exist yet, so openInfoWindow will fail.
    // given a selected school, select its region or ward and trigger a redraw
    async function allPolygonAdjust(school) {
        if ($targets.showAllRegions.data("show-areas") === true) {
            $targets.regionSelect.val(school.regionId);
            $targets.showAllRegions.data("show-areas", false);
            await updateFilters();
        } else if ($targets.showAllWards.data("show-areas") === true) {
            $targets.wardSelect.val(school.wardId);
            $targets.showAllWards.data("show-areas", false);
            await updateFilters();
        }
    }

    // populates the info window
    function openInfoWindow(marker) {
        const position = marker.position;

        // have the info window appear in the center of the circles and offset higher on icons.
        infoWindow.setOptions({
            pixelOffset: new google.maps.Size(0, -26),
            maxWidth: 600,
        });
        infoWindow.setHeaderContent(infoWindowHeaderContent(marker));
        infoWindow.setContent(infoWindowContent(marker));
        infoWindow.setPosition(position);
        infoWindow.open(map);

        // remove the selection highlight from the "X" in the newly opened window
        infoWindow.addListener("visible", () => {
            $(".gm-style-iw-chr button[title=Close]").trigger("blur");
            $(".infowindow-content a").trigger("blur");
        });

        map.setZoom(15);
        map.setCenter(position);
        positionMarkersOnMap(map, $targets);


        function infoWindowHeaderContent(m) {
            const $header = $("<div>").addClass("infowindow-content mb-2");
            $header.append($("<h4>").html(m.school.schoolName).addClass("mb-1"));
            $header.append($("<div>").html(schoolTypeText(m.type)));
            const $address = $("<div>").html($addressDirectionLink(m.school.schoolAddress));
            $header.append($address);

            const $phoneLink = $("<a>").attr({"href": `tel:${m.school.schoolPhone.replace(/\D/g, "")}`}).html(phoneNumberMask(m.school.schoolPhone));
            $header.append($("<div>").html($phoneLink));

            if (m.school.fadUrl) {
                const $region = $("<div>")
                const $regionLink = $("<a>").attr({
                    "href": `${m.school.fadUrl}`, "target": "_blank"
                }).html("Facilities Assessment Report");
                $region.html($regionLink);
                $header.append($region);
            }
            return $header[0];

            // expand the school type code from the vacancy api for display
            function schoolTypeText(type) {
                switch (type) {
                    case "HS":
                        return "High School";
                    case "ES":
                        return "Elementary School";
                    case "MS":
                        return "Middle School";
                    default:
                        return "School";
                }
            }
        }

        function infoWindowContent(m) {
            const school = m.school;
            const $content = $("<div>").addClass("infowindow-content container-fluid px-0");

            // arrange the tables
            const $row1 = $("<div>").addClass("row mb-2 g-0");
            const $row2 = $("<div>").addClass("row g-0");
            $row1.html($("<div>").addClass("col").html($tableFacilities()));
            $row2.html($("<div>").addClass("col").html($tableUtilization()));
            $row2.append($("<div>").addClass("col").html($tableProjects()));
            $content.append($row1, $row2);

            return $content[0];

            function $tableFacilities() {
                const $table = $("<table>").attr({"style": "width: 500px;"}).addClass("table table-striped table-sm");
                // create the header for the main table
                const $tableHead = $("<thead>");

                const $tableHeadRow = $("<tr>");
                $tableHeadRow.html($("<th>").html("Current Facility Deficiencies"));
                $tableHeadRow.append($("<th>").html("Immediate"));
                $tableHeadRow.append($("<th>").html("Long Term"));
                $tableHeadRow.append($("<th>").html("Other"));

                const $tableBody = $("<tbody>");
                const $tableRow1 = $("<tr>");
                $tableRow1.html($("<td>").html("Roof/Masonry System"));
                $tableRow1.append($("<td>").html($checkMark(school.immediateRoofMasonrySystem)));
                $tableRow1.append($("<td>").html($checkMark(school.longTermRoofMasonrySystem)));
                $tableRow1.append($("<td>"));

                const $tableRow2 = $("<tr>");
                $tableRow2.html($("<td>").html("Window System"));
                $tableRow2.append($("<td>").html($checkMark(school.immediateWindowSystem)));
                $tableRow2.append($("<td>").html($checkMark(school.longTermWindowSystem)));
                $tableRow2.append($("<td>"));

                const $tableRow3 = $("<tr>");
                $tableRow3.html($("<td>").html("MEP System"));
                $tableRow3.append($("<td>").html($checkMark(school.immediateMepSystem)));
                $tableRow3.append($("<td>").html($checkMark(school.longTermMepSystem)));
                $tableRow3.append($("<td>"));

                const $tableRow4 = $("<tr>");
                $tableRow4.html($("<td>").html("Plumbing System"));
                $tableRow4.append($("<td>").html($checkMark(school.immediatePlumbingSystem)));
                $tableRow4.append($("<td>").html($checkMark(school.longTermPlumbingSystem)));
                $tableRow4.append($("<td>"));

                const $tableRow5 = $("<tr>");
                $tableRow5.html($("<td>").html("Fire Alarm System"));
                $tableRow5.append($("<td>").html($checkMark(school.immediateFireAlarmSystem)));
                $tableRow5.append($("<td>").html($checkMark(school.longTermFireAlarmSystem)));
                $tableRow5.append($("<td>"));

                const $tableRow6 = $("<tr>");
                $tableRow6.html($("<td>").html("Building Upgrades"));
                $tableRow6.append($("<td>"));
                $tableRow6.append($("<td>"));
                $tableRow6.append($("<td>").html($checkMark(school.buildingUpgrades)));

                const $tableRow7 = $("<tr>");
                $tableRow7.html($("<td>").html("ADA"));
                $tableRow7.append($("<td>"));
                $tableRow7.append($("<td>"));
                $tableRow7.append($("<td>").html($checkMark(school.adaCompliance)));

                const total = parseFloat(school.currentFacilityTotalMillions / 1000000);

                const $tableRow8 = $("<tr>");
                $tableRow8.html($("<td>").html($strong("Total (in millions)")));
                $tableRow8.append($("<td>").attr({
                    "colspan": 3, "align": "right"
                }).html(`${USMillionDollar.format(total)}M`));

                $tableHead.append($tableHeadRow);
                $tableBody.append($tableRow1, $tableRow2, $tableRow3, $tableRow4, $tableRow5, $tableRow6, $tableRow7, $tableRow8);

                return $table.append($tableHead, $tableBody);
            }

            function $tableUtilization() {
                const $out = $("<div>").addClass("pe-1");
                const $table = $("<table>").addClass("table table-striped table-sm mb-0");
                // create the header for the main table
                const $tableHead = $("<thead>");

                const $tableHeadRow = $("<tr>").html($("<th>").attr({"colspan": 2}).html("Utilization"));

                const $tableBody = $("<tbody>");
                const $tableRow1 = $("<tr>");
                $tableRow1.html($("<td>").html("Attending SY23-24"));
                $tableRow1.append($("<td>").html(school.totalAttendingSy202223 && school.totalAttendingSy202223.toLocaleString()));

                const $tableRow2 = $("<tr>");
                $tableRow2.html($("<td>").html("Ideal Capacity"));
                $tableRow2.append($("<td>").html(school.idealCapacity && school.idealCapacity.toLocaleString()));

                const $tableRow3 = $("<tr>");
                $tableRow3.html($("<td>").html("Status"));
                $tableRow3.append($("<td>").html(school.utilizationStatus));

                const $tableRow4 = $("<tr>");
                $tableRow4.html($("<td>").html("Square Feet"));
                $tableRow4.append($("<td>").html(school.squareFootage && school.squareFootage.toLocaleString()));

                $tableHead.append($tableHeadRow);
                $tableBody.append($tableRow1, $tableRow2, $tableRow3, $tableRow4);

                return $out.html($table.append($tableHead, $tableBody));
            }

            function $tableProjects() {
                const $out = $("<div>").addClass("ps-1");
                const $table = $("<table>").addClass("table table-striped table-sm mb-0");
                // create the header for the main table
                const $tableHead = $("<thead>");

                const $tableHeadRow = $("<tr>").html($("<th>").attr({"colspan": 2}).html("Capital Projects 2013-23"));

                const $tableBody = $("<tbody>");
                const $tableRow1 = $("<tr>");
                $tableRow1.html($("<td>").html("Exterior"));
                $tableRow1.append($("<td>").append($checkMark(school.exteriorEnvelope)));

                const $tableRow2 = $("<tr>");
                $tableRow2.html($("<td>").html("Interior"));
                $tableRow2.append($("<td>").append($checkMark(school.interior)));

                const $tableRow3 = $("<tr>");
                $tableRow3.html($("<td>").html("MEP+FP System"));
                $tableRow3.append($("<td>").append($checkMark(school.mepFp)));

                const $tableRow4 = $("<tr>");
                $tableRow4.html($("<td>").html("New Construction"));
                $tableRow4.append($("<td>").append($checkMark(school.newConstruction)));

                const $tableRow5 = $("<tr>");
                $tableRow5.html($("<td>").html("Site"));
                $tableRow5.append($("<td>").append($checkMark(school.site)));

                const $tableRow6 = $("<tr>");
                $tableRow6.html($("<td>").append($strong("Total")));
                $tableRow6.append($("<td>").html(USDollar.format(school.capitalProjectGrandTotal)));

                $tableBody.append($tableRow1);
                $tableBody.append($tableRow2);
                $tableBody.append($tableRow3);
                $tableBody.append($tableRow4);
                $tableBody.append($tableRow5);
                $tableBody.append($tableRow6);

                $tableHead.append($tableHeadRow);

                $table.append($tableHead, $tableBody);

                return $out.html($table);
            }
        }

        function phoneNumberMask(phone) {
            return phone.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3")
        }
    }
} catch (e) {
    console.error(e);
}